import React, { useState } from "react";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { Button } from "components/common/Button";
import { formatDayMonthYear, toUtcWall } from "@/lib/dates";
import { ConsentModal } from "./ConsentModal";
import { tos } from "./legalContent";
import { useConsentStage } from "./consentStage";
import { useTermsPromptStore } from "@/stores/useTermsPromptStore";

const DISMISSED_KEY = "termsBannerDismissedOn";

function today(): string {
  return new Date().toDateString();
}

function dismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === today();
  } catch {
    return false;
  }
}

/// Changed Terms during their grace period, or a changed Privacy Policy: a
/// line above the page, never a wall. "Later" hides it until tomorrow;
/// `TermsReviewRow` in Account settings stays the way back. It renders the one
/// dialog every Review and every `terms_not_accepted` refusal opens.
const REVIEW_BUTTON_CLASS = "button-active-state border-transparent!";

const reviewMessage = (ppAccepted: boolean) =>
  `We've updated our Terms of Service${ppAccepted ? "" : " and Privacy Policy"}. Please review and accept ${ppAccepted ? "it" : "them"} before ${formatDayMonthYear(toUtcWall(tos.acceptBy))}.`;

export const TermsBanner: React.FC = () => {
  const { ppAccepted, setPpAccepted } = useAuth();
  const stage = useConsentStage();
  const [dismissed, setDismissed] = useState(dismissedToday);
  const { open, reason, ask, close } = useTermsPromptStore();

  if (stage !== "review" && stage !== "notice") return null;

  const later = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, today());
    } catch {
      // Without storage it hides for this visit only.
    }
    setDismissed(true);
  };

  const acknowledge = async () => {
    try {
      await axiosInstance.post("/action/auth/accept-consent", {
        consent_types: ["privacy_policy"],
      });
      setPpAccepted(true);
    } catch {
      // Stays up; the next visit asks again.
    }
  };

  const message =
    stage === "review"
      ? reviewMessage(ppAccepted)
      : "We've updated our Privacy Policy.";

  return (
    <>
      {!dismissed && (
        <div className="org-deletion-banner notice" role="status">
          <div className="banner-dot" />
          <span className="flex-1">{message}</span>
          <Button
            variant="default"
            size="xs"
            className={REVIEW_BUTTON_CLASS}
            onClick={() => ask()}
          >
            {stage === "review" ? "Review" : "Read"}
          </Button>
          {stage === "review" ? (
            <Button variant="quiet-link" size="xs" onClick={later}>
              Later
            </Button>
          ) : (
            <Button variant="quiet-link" size="xs" onClick={acknowledge}>
              Got it
            </Button>
          )}
        </div>
      )}
      {open && <ConsentModal reason={reason} onClose={close} />}
    </>
  );
};

export const TermsReviewRow: React.FC = () => {
  const { ppAccepted } = useAuth();
  const ask = useTermsPromptStore((s) => s.ask);
  if (useConsentStage() !== "review") return null;

  // The banner's own look, as a card in Account settings rather than a line above the page.
  return (
    <div
      className="org-deletion-banner notice rounded-app! border-b-0!"
      role="status"
    >
      <div className="banner-dot" />
      <span className="flex-1">{reviewMessage(ppAccepted)}</span>
      <Button
        variant="default"
        size="xs"
        className={REVIEW_BUTTON_CLASS}
        onClick={() => ask()}
      >
        Review
      </Button>
    </div>
  );
};
